import { useEffect, useState } from "react";
import { AlertCircle, MessageSquare } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useComments, useAddComment, useUpdateComment, useDeleteComment } from "@/hooks/useComments";
import { CommentForm } from "./CommentForm";
import { CommentItem } from "./CommentItem";

interface PriorityCommentSectionProps {
  priorityPid: string;
  enabled: boolean;
  onCountChange?: (count: number) => void;
}

export function PriorityCommentSection({
  priorityPid,
  enabled,
  onCountChange,
}: PriorityCommentSectionProps) {
  const { data, isLoading, isError } = useComments(priorityPid, 1, 50, enabled);
  const addComment = useAddComment(priorityPid);
  const updateComment = useUpdateComment(priorityPid);
  const deleteComment = useDeleteComment(priorityPid);

  const [updatingPid, setUpdatingPid] = useState<string | null>(null);
  const [deletingPid, setDeletingPid] = useState<string | null>(null);

  const comments = data?.data || [];
  const total = data?.total ?? 0;

  useEffect(() => {
    onCountChange?.(total);
  }, [total, onCountChange]);

  return (
    <div className="flex flex-col">
      <div className="mb-3 mt-2 shrink-0">
        <CommentForm
          onSubmit={(content) => addComment.mutate(content)}
          isSubmitting={addComment.isPending}
        />
      </div>

      {/* Comments list */}
      {isLoading ? (
        <div className="space-y-3 mt-1">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-3 py-2">
              <Skeleton className="h-8 w-8 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-transparent py-10 text-center">
          <AlertCircle className="mb-2 h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-foreground mb-1">Couldn't load comments</p>
          <p className="text-xs text-muted-foreground">Check your connection and try again.</p>
        </div>
      ) : comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-transparent py-10 text-center">
          <MessageSquare className="mb-2 h-8 w-8 text-muted-foreground/80" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground mb-1">No comments yet</p>
          <p className="text-xs text-muted-foreground max-w-[220px]">
            Add a comment to share context or updates with your team.
          </p>
        </div>
      ) : (
        <ScrollArea className="max-h-[400px]">
        <div className="-mx-1 space-y-1.5 px-1 pb-3">
          {comments.map((comment) => (
            <CommentItem
              key={comment.pid}
              comment={comment}
              onUpdate={(commentPid, content, onSuccess) => {
                setUpdatingPid(commentPid);
                updateComment.mutate(
                  { commentPid, content },
                  {
                    onSuccess: () => {
                      setUpdatingPid(null);
                      onSuccess();
                    },
                    onError: () => setUpdatingPid(null),
                  },
                );
              }}
              onDelete={(commentPid) => {
                setDeletingPid(commentPid);
                deleteComment.mutate(commentPid, {
                  onSuccess: () => setDeletingPid(null),
                  onError: () => setDeletingPid(null),
                });
              }}
              isUpdating={updatingPid === comment.pid}
              isDeleting={deletingPid === comment.pid}
            />
          ))}
        </div>
        </ScrollArea>
      )}
    </div>
  );
}
